import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { bookingKeys, jobKeys, schedulerKeys } from '@/lib/queryKeys';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { apiPatch, operationsClient } from '@/lib/api';
import { transformBooking } from './useBooking';
import type { Booking, UpdateBookingDto } from '@/types/booking';

export function useUpdateBooking() {
  const queryClient = useQueryClient();

  return useApiMutation<Booking, UpdateBookingDto>({
    mutationFn: async ({ id, ...dto }) => {
      const raw = await apiPatch<Record<string, unknown>, Omit<UpdateBookingDto, 'id'>>(
        operationsClient,
        schedulerEndpoints.booking(id),
        dto,
      );
      return transformBooking(raw);
    },
    onMutate: async (updated) => {
      await queryClient.cancelQueries({ queryKey: bookingKeys.detail(updated.id) });
      const previous = queryClient.getQueryData<Booking>(bookingKeys.detail(updated.id));
      if (previous) {
        queryClient.setQueryData<Booking>(bookingKeys.detail(updated.id), {
          ...previous,
          ...updated,
        });
      }
      return { previous };
    },
    onError: (_err, updated, context) => {
      const ctx = context as { previous?: Booking } | undefined;
      if (ctx?.previous) {
        queryClient.setQueryData(bookingKeys.detail(updated.id), ctx.previous);
      }
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: bookingKeys.detail(data.id) });
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.bookings() });
      if (data.jobId) {
        void queryClient.invalidateQueries({ queryKey: jobKeys.appointments(data.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.detail(data.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      }
      toast.success('Service call updated');
    },
  });
}
